import React, { useEffect, useMemo, useRef, useState } from 'react';
import type * as MapLibre from 'maplibre-gl';
import {
  loadEngine, numberedMarkerElement, dotMarkerElement, clusterMarkerElement,
  STYLE_URLS, cartoTransformRequest, creditElement, tokenColor,
  ENGINE_MAX_ZOOM, ENGINE_MIN_ZOOM,
} from '../utils/mapUtils';
import type { MapLibreNamespace, MarkerElement } from '../utils/mapUtils';
import { cameraForBounds } from '../utils/mercator';
import { toEngineZoom } from './explore/viewport';
import './UnifiedMap.css';

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  label: string;
  sublabel?: string;
  color?: string;
}

interface UnifiedMapProps {
  markers: MapMarker[];
  showRoute?: boolean;
  mode?: 'pins' | 'dots' | 'clusters';
  onMarkerClick?: (marker: MapMarker) => void;
}

type DisplayMarker = MapMarker & { _count?: number };

/** A single pin is framed at this engine zoom (Leaflet's 14). It is a FIT cap,
 *  applied as a camera command; interaction stays free to ENGINE_MAX_ZOOM. */
export const SINGLE_PIN_ZOOM = toEngineZoom(14);
/** Space kept clear around a multi-pin fit, as before. */
export const FIT_PADDING = 50;

const ROUTE_SOURCE = 'unified-map-route';
const ROUTE_LAYER = 'unified-map-route-line';

const popupContent = (m: MapMarker): HTMLElement => {
  const root = document.createElement('div');
  root.className = 'unified-map__popup';
  const title = document.createElement('strong');
  title.textContent = m.label;
  root.appendChild(title);
  if (m.sublabel) {
    const sub = document.createElement('span');
    sub.className = 'unified-map__popup-sub';
    sub.textContent = m.sublabel;
    root.appendChild(sub);
  }
  return root;
};

const UnifiedMap: React.FC<UnifiedMapProps> = ({ markers, showRoute = true, mode = 'pins', onMarkerClick }) => {
  // For cluster mode, group markers by label and compute centroids
  const displayMarkers = useMemo((): DisplayMarker[] => {
    if (mode !== 'clusters') return markers;

    const groups: Record<string, { markers: MapMarker[]; totalLat: number; totalLng: number }> = {};
    for (const m of markers) {
      const key = m.label;
      if (!groups[key]) groups[key] = { markers: [], totalLat: 0, totalLng: 0 };
      groups[key].markers.push(m);
      groups[key].totalLat += m.lat;
      groups[key].totalLng += m.lng;
    }

    return Object.entries(groups)
      .filter(([, g]) => g.markers.length >= 2) // Drop destinations with <2
      .map(([label, g]) => ({
        id: `cluster-${label}`,
        lat: g.totalLat / g.markers.length,
        lng: g.totalLng / g.markers.length,
        label,
        sublabel: `${g.markers.length} activities`,
        color: g.markers[0].color || 'var(--text-primary)',
        _count: g.markers.length,
      }));
  }, [markers, mode]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [engine, setEngine] = useState<{ ns: MapLibreNamespace; map: MapLibre.Map } | null>(null);
  const [styleReady, setStyleReady] = useState(false);
  const clickRef = useRef(onMarkerClick);
  clickRef.current = onMarkerClick;

  const hasMarkers = displayMarkers.length > 0;

  // The map: created once the engine has loaded (and its worker URL is set),
  // removed on unmount.
  useEffect(() => {
    if (!hasMarkers) return undefined;
    let alive = true;
    let map: MapLibre.Map | null = null;
    let credit: HTMLElement | null = null;
    loadEngine().then((ns) => {
      const container = containerRef.current;
      if (!alive || !container) return;
      // Without WebGL this throws; the trailing catch below takes it.
      map = new ns.Map({
        container,
        style: STYLE_URLS.base,
        transformRequest: cartoTransformRequest(),
        attributionControl: false,
        scrollZoom: false,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        maxZoom: ENGINE_MAX_ZOOM,
        minZoom: ENGINE_MIN_ZOOM,
      });
      // Leaflet's default +/- stays (wheel zoom stays off); rotation and pitch
      // stay off (plan §1 non-goal, and the bearing-0 camera arithmetic of D9).
      map.addControl(new ns.NavigationControl({ showCompass: false, showZoom: true }), 'top-left');
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation();
      credit = container.appendChild(creditElement());
      map.on('load', () => { if (alive) setStyleReady(true); });
      setEngine({ ns, map });
    }).catch(() => {
      // Loading or initialisation failed: the engine chunk did not load, or
      // the map could not start (no WebGL, so the constructor threw). The pane
      // stays empty rather than taking the page down with it.
    });
    return () => {
      alive = false;
      if (map) map.remove();
      if (credit) credit.remove();
      setEngine(null);
      setStyleReady(false);
    };
  }, [hasMarkers]);

  // Markers, and the camera that frames them.
  useEffect(() => {
    if (!engine) return undefined;
    const { ns, map } = engine;
    const placed = displayMarkers.map((m, i) => {
      const built: MarkerElement = mode === 'pins' ? numberedMarkerElement(i + 1)
        : mode === 'dots' ? dotMarkerElement(m.color || 'var(--text-primary)')
          : clusterMarkerElement(m._count || 0, m.color || 'var(--text-primary)');
      built.element.addEventListener('click', () => clickRef.current?.(m));
      const popup = new ns.Popup({ offset: built.popupOffset }).setDOMContent(popupContent(m));
      return new ns.Marker({ element: built.element, anchor: 'center' })
        .setLngLat([m.lng, m.lat])
        .setPopup(popup)
        .addTo(map);
    });

    if (displayMarkers.length > 0) {
      const lats = displayMarkers.map((m) => m.lat);
      const lngs = displayMarkers.map((m) => m.lng);
      const rect = map.getContainer().getBoundingClientRect();
      const single = displayMarkers.length === 1;
      const camera = cameraForBounds(
        {
          west: Math.min(...lngs), south: Math.min(...lats),
          east: Math.max(...lngs), north: Math.max(...lats),
        },
        { width: rect.width, height: rect.height },
        {
          padding: single ? 0 : FIT_PADDING,
          maxZoom: single ? SINGLE_PIN_ZOOM : ENGINE_MAX_ZOOM,
          minZoom: ENGINE_MIN_ZOOM,
        },
      );
      map.jumpTo({ center: [camera.centre[1], camera.centre[0]], zoom: camera.zoom });
    }

    return () => { placed.forEach((marker) => marker.remove()); };
  }, [engine, displayMarkers, mode]);

  // The route line — pins mode only — drawn once the style has loaded.
  // `hasMarkers` guards the populated-to-empty render: the map effect's cleanup
  // has just called `map.remove()`, but this render still holds the old
  // `engine` and `styleReady`, and a removed map must never be touched again
  // (a source added to it builds a new style that nothing ever cleans up).
  useEffect(() => {
    if (!engine || !styleReady || !hasMarkers) return;
    const { map } = engine;
    const coordinates = mode === 'pins' && showRoute && displayMarkers.length > 1
      ? displayMarkers.map((m) => [m.lng, m.lat]) : [];
    const data: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: coordinates.length > 1 ? [{
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates },
      }] : [],
    };
    const source = map.getSource(ROUTE_SOURCE) as MapLibre.GeoJSONSource | undefined;
    if (source) {
      source.setData(data);
      return;
    }
    map.addSource(ROUTE_SOURCE, { type: 'geojson', data });
    map.addLayer({
      id: ROUTE_LAYER,
      type: 'line',
      source: ROUTE_SOURCE,
      paint: {
        'line-color': tokenColor('--text-primary'),
        'line-width': 3,
        'line-opacity': 0.7,
        // Leaflet's "10, 10" at weight 3; the engine counts in line widths.
        'line-dasharray': [10 / 3, 10 / 3],
      },
    });
  }, [engine, styleReady, hasMarkers, displayMarkers, mode, showRoute]);

  if (!hasMarkers) return null;

  return (
    <div className="unified-map">
      <div ref={containerRef} className="unified-map__container" data-testid="unified-map-canvas" />
    </div>
  );
};

export default UnifiedMap;
